import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import {
  getBulkOperations,
  createBulkOperation,
} from '../apis/bulkOperationApi';
import { CreateBulkOperationRequest } from '../apis/types';

export const useBulkOperations = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  // Query for fetching bulk operations
  const {
    data: bulkOperations = [],
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ['bulk-operations'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getBulkOperations(token);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Mutation for creating a new bulk operation
  const createBulkOperationMutation = useMutation({
    mutationFn: async (request: CreateBulkOperationRequest) => {
      const token = await getAccessTokenSilently();
      return createBulkOperation(request, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulk-operations'] });
    },
  });

  const error = queryError?.message || null;

  return {
    bulkOperations,
    loading,
    error,
    refreshBulkOperations: () => {
      queryClient.invalidateQueries({ queryKey: ['bulk-operations'] });
    },
    createBulkOperation: createBulkOperationMutation.mutate,
    createBulkOperationAsync: createBulkOperationMutation.mutateAsync,
    isCreating: createBulkOperationMutation.isPending,
    createError: createBulkOperationMutation.error?.message || null,
  };
};
